import React, { useMemo } from "react";

import { LoadingIcon as Loading, InfoSmIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import LabeledInfo from "Components/LabeledInfo";
import ProjectMoreLinks from "Components/ProjectMoreLinks";
import { Button, ButtonWrapper, IconControl, Link } from "ds/Button";
import Dialog from "ds/Dialog/Dialog";
import { Chevron } from "ds/Dropdown";
import { capitalize, getSubscriptionEditUrl } from "Libs/utils";
import { gitProjectSelector } from "Reducers/project/project";
import useBlackFireSellableStatus from "src/organization/pages/project/containers/ProjectOverview/BlackfireCard/useBlackFireSellableStatus";
import { useAppSelector } from "Store/hooks";

import * as S from "./styles";

import type {
  OrganizationSubscription,
  SubscriptionEstimateComplex
} from "@packages/client";

type PlanDetailPropType = {
  subscription: OrganizationSubscription;
  estimate?: SubscriptionEstimateComplex;
  editUrl?: { external: boolean; url: string };
  onOpen: () => void;
  isOpen?: boolean;
};

const PlanDetail = (props: PlanDetailPropType) => {
  const intl = useIntl();

  const { subscription, estimate, onOpen, isOpen } = props;
  const {
    project_region_label,
    environments,
    big_dev,
    plan,
    user_licenses,
    support_tier = "",
    storage,
    project_title,
    observability_suite,
    blackfire,
    project_id,
    organization_id,
    owner,
    id
  } = subscription ?? {};

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId: organization_id || owner || "",
      projectId: project_id || ""
    })
  );

  const editUrl = useMemo(
    () => getSubscriptionEditUrl({ subscription }),
    [subscription]
  );

  const { isEnterprise } = useBlackFireSellableStatus(subscription);

  return (
    <S.Wrapper
      className={`plan-detail-wrapper-${id} ${
        isOpen ? "plan-detail-open" : ""
      }`}
    >
      <S.HeadingContainer className={`head ${isOpen ? "open" : ""}`}>
        <S.Heading className="narrow">
          <S.ProjectTitle>{project_title}</S.ProjectTitle>
        </S.Heading>
        <S.Heading className="wide">
          <S.ProjectTitle>{project_title}</S.ProjectTitle>
          <S.CommonSpan>
            {intl.formatMessage(
              {
                id: "billing.plan.support"
              },
              {
                type: support_tier
              }
            )}
          </S.CommonSpan>
          <S.ProjectPlan>{plan}</S.ProjectPlan>
          <S.ProjectRegion>{project_region_label}</S.ProjectRegion>
        </S.Heading>

        <div className="control">
          <ProjectMoreLinks
            viewLink
            iconColor="black"
            from="plans"
            project={project}
            subscription={subscription}
          />

          {/* when the user doesn't have "Manage plans" permissions the organization subscription estimate api will fail and therefore Console should hide this button */}
          {editUrl?.url && (
            <IconControl onClick={onOpen} aria-label="plan-chevron-icon">
              <Chevron className="open-chevron" />
            </IconControl>
          )}
        </div>
      </S.HeadingContainer>

      {estimate ? (
        <S.AccordionItem className={isOpen ? "open" : ""}>
          <S.GridList>
            <div>
              <LabeledInfo label="Region" value={project_region_label} />
            </div>
            <div>
              <LabeledInfo label="Environments" value={environments} />
            </div>
            <div>
              <LabeledInfo
                label={intl.formatMessage({
                  id: "environment_application_size"
                })}
                value={big_dev || "N/A"}
              />
            </div>
            <div>
              <LabeledInfo
                label={intl.formatMessage({ id: "plan" })}
                value={plan}
              />
            </div>
          </S.GridList>
          <S.GridList>
            <div>
              <LabeledInfo
                label={intl.formatMessage({ id: "storage" })}
                value={`${storage / 1024} GB`}
              />
            </div>
            <div>
              <LabeledInfo
                label={intl.formatMessage({ id: "users" })}
                value={user_licenses}
              />
            </div>
            <div>
              <LabeledInfo label="support" value={support_tier} />
            </div>
            <div>
              <LabeledInfo
                label={intl.formatMessage({
                  id: isEnterprise
                    ? "integration.observability_suite"
                    : "blackfire.title"
                })}
                value={
                  (isEnterprise ? observability_suite : blackfire) ||
                  intl.formatMessage({
                    id: "not_included"
                  })
                }
              />
            </div>
          </S.GridList>

          {isEnterprise && (
            <S.GridList>
              <div>
                <LabeledInfo
                  label={intl.formatMessage({
                    id: "project.details.origin_requests"
                  })}
                  value={estimate.ingress_requests.current_usage_formatted.replace(
                    " requests",
                    ""
                  )}
                  infoDialog
                  infoDialogAlign="start"
                  infoDialogText={intl.formatMessage({
                    id: "project.billing.origin_traffic_note.short"
                  })}
                  shouldCapitalize={false}
                />
              </div>
              <div>
                <LabeledInfo
                  label={intl.formatMessage({
                    id: "project.details.origin_bandwidth"
                  })}
                  value={estimate.egress_bandwidth.current_usage_formatted}
                  infoDialog
                  infoDialogAlign="start"
                  infoDialogText={intl.formatMessage({
                    id: "project.billing.origin_traffic_note.short"
                  })}
                  shouldCapitalize={false}
                />
              </div>
              <div>
                {estimate.fastly_requests && (
                  <LabeledInfo
                    label={intl.formatMessage({
                      id: "project.details.cdn_requests"
                    })}
                    value={
                      estimate.fastly_requests.current_usage_formatted.replace(
                        " requests",
                        ""
                      ) ?? ""
                    }
                    infoDialog
                    infoDialogAlign="start"
                    infoDialogText={intl.formatMessage({
                      id: "project.billing.cdn_traffic_note.short"
                    })}
                    shouldCapitalize={false}
                  />
                )}
              </div>
              <div>
                {estimate.fastly_bandwidth && (
                  <LabeledInfo
                    label={intl.formatMessage({
                      id: "project.details.cdn_bandwidth"
                    })}
                    value={
                      estimate.fastly_bandwidth.current_usage_formatted ?? ""
                    }
                    infoDialog
                    infoDialogAlign="start"
                    infoDialogText={intl.formatMessage({
                      id: "project.billing.cdn_traffic_note.short"
                    })}
                    shouldCapitalize={false}
                  />
                )}
              </div>
            </S.GridList>
          )}

          <S.GridList>
            <S.Total>
              {intl.formatMessage({ id: "total_monthly_cost" })}*{" "}
              <Dialog
                description={intl.formatMessage({
                  id: "billing_system_explanation"
                })}
                title={intl.formatMessage({
                  id: "estimated_cost"
                })}
                analyticId="billing_system_explanation"
                trigger={
                  <IconControl aria-label="info-trigger-billing_system_explanation">
                    <InfoSmIcon />
                  </IconControl>
                }
              />
              <S.Amount>{estimate.total.formatted}</S.Amount>
            </S.Total>
          </S.GridList>
          <S.GridList>
            {editUrl && (
              <ButtonWrapper>
                {editUrl.external ? (
                  <Link href={editUrl.url} analyticId="edit_plan">
                    {capitalize(
                      intl.formatMessage({
                        id: "edit_plan"
                      })
                    )}
                  </Link>
                ) : (
                  <Button
                    to={editUrl.url}
                    state={{ from: "plans" }}
                    analyticId="edit_plan"
                  >
                    {capitalize(
                      intl.formatMessage({
                        id: "edit_plan"
                      })
                    )}
                  </Button>
                )}
              </ButtonWrapper>
            )}
          </S.GridList>
        </S.AccordionItem>
      ) : isOpen ? (
        <S.LoadingWrapper>
          <Loading />
        </S.LoadingWrapper>
      ) : null}
    </S.Wrapper>
  );
};

export default PlanDetail;
